import { initialOptionLayoutState, OptionLayoutState } from './option-layout.store';
import { computed, effect } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
  withHooks
} from '@ngrx/signals';
import { initialSubscriberState, SubscriberState } from './subscribers.store';
import { PathType } from '../shared/enum/path-type.enum';
import { initialOfferState, OfferState } from './offer.store';
import { DirectDebitState, initialDirectDebitState } from './direct-debit.store';
import { initialSubscriptionState, SubscriptionState } from './steps.store';
import { ContextState, initialContextState } from './context.store';
import { DevisState, initialDevisState } from './devis.store';
import { ContractState, initialContractState } from './contract.store';
import { Place } from '../shared/models/model/concretes/shared/place.model';
import {
  ElectronicSignatureState,
  initialElectronicSignatureState
} from './electronic-signature.store';
import { PhonePrefixState } from './phone-prefix.store';
import { DeclarationState, initialDeclarationState } from './declaration.store';

export interface CombinedState {
  context: ContextState;
  path: PathType;
  option_layout: OptionLayoutState;
  declaration: DeclarationState;
  subscriber: SubscriberState;
  offer: OfferState;
  direct_debit: DirectDebitState;
  devis: DevisState;
  contract: ContractState;
  electronic_signature: ElectronicSignatureState;
  subscription: SubscriptionState | null;
}

const initialState: CombinedState = {
  context: initialContextState,
  path: PathType.UNDEFINED,
  option_layout: initialOptionLayoutState,
  declaration: initialDeclarationState,
  subscriber: initialSubscriberState,
  offer: initialOfferState,
  direct_debit: initialDirectDebitState,
  devis: initialDevisState,
  contract: initialContractState,
  electronic_signature: initialElectronicSignatureState,
  subscription: null
};

export const CombinedStore = signalStore(
  { providedIn: 'root' },
  withState(getStateFromLocalStorage()),
  withComputed(store => ({
    concatene: computed(
      () =>
        `${store.declaration.name()} - ${store.declaration.type()} - ${store.declaration.race()} - ${store.declaration.sexe()}`
    ),
    countDiseases: computed(() => store.declaration().diseaces.length),
    birth_date_formatted: computed(
      () => `${store.declaration.birth_date()?.toLocaleString('fr-FR').substring(0, 10)}`
    ),
    effect_date_formatted: computed(
      () => `${store.declaration.effect_date()?.toLocaleString('fr-FR').substring(0, 10)}`
    ),
    birth_date_subscriber_formatted: computed(
      () => `${store.subscriber.birth_date()?.toLocaleString('fr-FR').substring(0, 10)}`
    ),
    start_date_direct_debit_formatted: computed(
      () => `${store.direct_debit.start_date()?.toLocaleString('fr-FR').substring(0, 10)}`
    )
  })),
  withMethods(store => ({
    select(): CombinedState {
      return getState(store);
    },
    updateContext(contextState: ContextState): void {
      patchState(store, { context: { ...store.context(), ...contextState } });
    },
    updateContextSalesChannel(salesChannel: string):void{
      patchState(store, { context: { ...store.context(), originalCanalVente: salesChannel } });
    },
    updateTokenContext(token: string): void {
      patchState(store, { context: { ...store.context(), token: token } });
    },
    resetContextState(): void {
      patchState(store, { context: { ...store.context(), ...initialContextState } });
    },
    updatePath(path: PathType): void {
      patchState(store, { path: path });
    },
    updateSubscriberCG(subscriberCG: boolean): void {
      patchState(store, {
        option_layout: { ...store.option_layout(), subscriberCG: subscriberCG }
      });
    },
    updateSizeXL(sizeXL: boolean): void {
      patchState(store, { option_layout: { ...store.option_layout(), sizeXL: sizeXL } });
    },
    updateHeader(header: boolean): void {
      patchState(store, { option_layout: { ...store.option_layout(), header: header } });
    },
    updateBreadcrumb(breadcrumb: boolean): void {
      patchState(store, { option_layout: { ...store.option_layout(), breadcrumb: breadcrumb } });
    },
    updateVerticalAlign(vertical_align: boolean): void {
      patchState(store, {
        option_layout: { ...store.option_layout(), vertical_align: vertical_align }
      });
    },
    updateStepCheckEmail(step_check_email: boolean): void {
      patchState(store, {
        option_layout: { ...store.option_layout(), step_check_email: step_check_email }
      });
    },
    updateFormErrorSubmit(form_error_submit: boolean): void {
      patchState(store, {
        option_layout: { ...store.option_layout(), form_error_submit: form_error_submit }
      });
    },
    updateOptionLayout(optionLayoutState: OptionLayoutState): void {
      patchState(store, { option_layout: { ...store.option_layout(), ...optionLayoutState } });
    },
    resetOptionLayoutState(): void {
      patchState(store, {
        option_layout: { ...store.option_layout(), ...initialOptionLayoutState }
      });
    },
    updateAddress(address: Place): void {
      patchState(store, {
        declaration: { ...store.declaration(), addressAutocomplete: address }
      });
    },
    updateIdentification(identification: string): void {
      patchState(store, {
        declaration: { ...store.declaration(), identification: identification }
      });
    },
    updateDiseases(diseases: string[]): void {
      patchState(store, { declaration: { ...store.declaration(), diseaces: diseases } });
    },
    updateDateEffect(date: Date): void {
      patchState(store, { declaration: { ...store.declaration(), effect_date: date } });
    },
    updateDeclaration(declaration: DeclarationState): void {
      patchState(store, { declaration: { ...store.declaration(), ...declaration } });
    },
    resetDeclarationState(): void {
      patchState(store, {
        declaration: { ...store.declaration(), ...initialDeclarationState }
      });
    },
    updateSubscriber(subscriberState: SubscriberState): void {
      patchState(store, { subscriber: { ...store.subscriber(), ...subscriberState } });
    },
    updateEmailSubscriber(email: string): void {
      patchState(store, { subscriber: { ...store.subscriber(), email: email } });
    },
    updateFromContextSubscriber(from_context: boolean): void {
      patchState(store, { subscriber: { ...store.subscriber(), from_context: from_context } });
    },
    updatePhonePrefixSubscriber(phone_prefix: PhonePrefixState): void {
      patchState(store, { subscriber: { ...store.subscriber(), phone_prefix: phone_prefix } });
    },
    resetSubscriberState(): void {
      patchState(store, { subscriber: { ...store.subscriber(), ...initialSubscriberState } });
    },
    updateOffer(offerState: OfferState): void {
      patchState(store, { offer: { ...store.offer(), ...offerState } });
    },
    updateSelectedOffer(selected_offer: string): void {
      patchState(store, { offer: { ...store.offer(), selected_offer: selected_offer } });
    },
    updateCouponCode(coupon_code: string): void {
      patchState(store, { offer: { ...store.offer(), coupon_code: coupon_code } });
    },
    updateTypePromotion(type_promotion: string): void {
      patchState(store, { offer: { ...store.offer(), type_promotion: type_promotion } });
    },
    resetCouponCode(): void {
      patchState(store, { offer: { ...store.offer(), coupon_code: '' } });
    },
    updateMensualPrice(mensual_price: string): void {
      patchState(store, { offer: { ...store.offer(), mensual_price: mensual_price } });
    },
    updateHighLighted(highlighted: boolean): void {
      patchState(store, { offer: { ...store.offer(), highlighted: highlighted } });
    },
    updateReferenceDevis(reference_devis: string): void {
      patchState(store, { offer: { ...store.offer(), reference_devis: reference_devis } });
    },
    resetOfferState(): void {
      patchState(store, { offer: { ...store.offer(), ...initialOfferState } });
    },
    updateDirectDebit(directDebitState: DirectDebitState): void {
      patchState(store, { direct_debit: { ...store.direct_debit(), ...directDebitState } });
    },
    updatePhonePrefixDirectDebit(phone_prefix: PhonePrefixState): void {
      patchState(store, { direct_debit: { ...store.direct_debit(), phone_prefix: phone_prefix } });
    },
    resetDirectDebitState(): void {
      patchState(store, { direct_debit: { ...store.direct_debit(), ...initialDirectDebitState } });
    },
    updateDevis(devisState: DevisState): void {
      patchState(store, { devis: { ...store.devis(), ...devisState } });
    },
    updateDevisClientAdvisorFullName(name: string): void {
      patchState(store, { devis: { ...store.devis(), ClientAdvisorFullName: name } });
    },
    resetDevisState(): void {
      patchState(store, { devis: { ...store.devis(), ...initialDevisState } });
    },
    updateContract(contractState: ContractState): void {
      patchState(store, { contract: { ...store.contract(), ...contractState } });
    },
    updateContractClientAdvisorFullName(name: string): void {
      patchState(store, { contract: { ...store.contract(), ClientAdvisorFullName: name } });
    },
    updateContractId(numeroContrat: string): void {
      patchState(store, { contract: { ...store.contract(), NumeroContrat: numeroContrat } });
    },
    updateContractReference(referenceContrat: string): void {
      patchState(store, { contract: { ...store.contract(), RefContrat: referenceContrat } });
    },
    resetContractState(): void {
      patchState(store, { contract: { ...store.contract(), ...initialContractState } });
    },
    updateElectronicSignature(electronicSignatureState: ElectronicSignatureState): void {
      patchState(store, {
        electronic_signature: { ...store.electronic_signature(), ...electronicSignatureState }
      });
    },
    resetElectronicSignatureState(): void {
      patchState(store, {
        electronic_signature: {
          ...store.electronic_signature(),
          ...initialElectronicSignatureState
        }
      });
    },
    updateSubscription(subscription: SubscriptionState | null): void {
      const newCombinedState = structuredClone(getStateFromLocalStorage());
      newCombinedState.subscription = subscription;
      patchState(store, { ...newCombinedState });
    },
    resetSubscription(): void {
      patchState(store, { subscription: { ...store.subscription(), ...initialSubscriptionState } });
    },
    update(combinedState: CombinedState): void {
      patchState(store, { ...combinedState });
    },
    reset(): void {
      patchState(store, { ...initialState });
    }
  })),
  withHooks({
    onInit(store) {
      effect(() => {
        saveState(store);
      });
    }
  })
);

function getState(store: any): CombinedState {
  try {
    return {
      context: {
        token: store.context.token(),
        codeOperation: store.context.codeOperation(),
        modeConnexion: store.context.modeConnexion(),
        referenceDevis: store.context.referenceDevis(),
        branche: store.context.branche(),
        produit: store.context.produit(),
        canalVente: store.context.canalVente(),
        originalCanalVente: store.context.originalCanalVente(),
        typeContact: store.context.typeContact(),
        referenceContrat: store.context.referenceContrat(),
        codeVendeur: store.context.codeVendeur(),
        avenants: store.context.avenants(),
        donneesPartenaire: store.context().donneesPartenaire,
        souscripteur: store.context().souscripteur
      },
      path: store.path(),
      option_layout: {
        header: store.option_layout.header(),
        resume_step: store.option_layout.resume_step(),
        breadcrumb: store.option_layout.breadcrumb(),
        footer: store.option_layout.footer(),
        webcallback: store.option_layout.webcallback(),
        subscriberCG: store.option_layout.subscriberCG(),
        sizeXL: store.option_layout.sizeXL(),
        vertical_align: store.option_layout.vertical_align(),
        step_check_email: store.option_layout.step_check_email(),
        form_error_submit: store.option_layout.form_error_submit()
      },
      declaration: {
        name: store.declaration.name(),
        addressAutocomplete: store.declaration.addressAutocomplete(),
        address: store.declaration.address(),
        postalCode: store.declaration.postalCode(),
        city: store.declaration.city(),
        country: store.declaration.country(),
        type: store.declaration.type(),
        race: store.declaration.race(),
        sexe: store.declaration.sexe(),
        birth_date: store.declaration.birth_date() ? store.birth_date_formatted() : null,
        chip_tatoo: store.declaration.chip_tatoo(),
        identification: store.declaration.identification(),
        vaccines: store.declaration.vaccines(),
        medical_history: store.declaration.medical_history(),
        diseaces: store.declaration.diseaces(),
        cover_level: store.declaration.cover_level(),
        effect_date: store.declaration.effect_date() ? store.effect_date_formatted() : null
      },
      subscriber: {
        email: store.subscriber.email(),
        civility: store.subscriber.civility(),
        lastname: store.subscriber.lastname(),
        firstname: store.subscriber.firstname(),
        birth_date: store.subscriber.birth_date() ? store.birth_date_subscriber_formatted() : null,
        birth_postal_code: store.subscriber.birth_postal_code(),
        birth_country: store.subscriber.birth_country(),
        birth_city: store.subscriber.birth_city(),
        nationality: store.subscriber.nationality(),
        phone_prefix: store.subscriber.phone_prefix(),
        consent: store.subscriber.consent(),
        is_client: store.subscriber.is_client(),
        from_context: store.subscriber.from_context()
      },
      offer: {
        selected_offer: store.offer.selected_offer(),
        coupon_code: store.offer.coupon_code(),
        mensual_price: store.offer.mensual_price(),
        highlighted: store.offer.highlighted(),
        reference_devis: store.offer.reference_devis(),
        type_promotion: store.offer.type_promotion()
      },
      direct_debit: {
        periodicity: store.direct_debit.periodicity(),
        start_date: store.direct_debit.start_date()
          ? store.start_date_direct_debit_formatted()
          : null,
        debit_day: store.direct_debit.debit_day(),
        iban: store.direct_debit.iban(),
        account_owner: store.direct_debit.account_owner(),
        phone_prefix: store.direct_debit.phone_prefix()
      },
      devis: store.devis(),
      contract: store.contract(),
      electronic_signature: store.electronic_signature(),
      subscription: store.subscription()
    };
  } catch (error) {
    console.log('Error in getState:', error);
    return initialState;
  }
}

function saveState(store: any) {
  saveStateInLocalStorage(getState(store));
}

function saveStateInLocalStorage(obj: CombinedState) {
  sessionStorage.setItem('combinedState', JSON.stringify(obj));
}

function getStateFromLocalStorage(): CombinedState {
  const state = sessionStorage.getItem('combinedState');
  return state ? JSON.parse(state) : initialState;
}
